import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import formReducer from "./slices/formSlice";
import themeReducer from "./slices/themeSlice";
import authReducer from "./slices/authSlice";
import layoutReducer from "./slices/layoutSlice";
import cartReducer from "./slices/cartSlice";
import salesCartReducer from "./slices/salesCartSlice";
import salesPriceBookCartReducer from "./slices/salesPriceBookCartSlice";
import preBookCartReducer from "./slices/preBookCartSlice";
import salesPreBookCartReducer from "./slices/salesPreBookCartSlice";
import globalPopupReducer from "./slices/globalPopupSlice";
import dashboardReducer from "./slices/dashboardSlice";
import salesDashboardReducer from "./slices/salesDashboardSlice";
import notificationReducer from "./slices/notificationSlice";
import navigationReducer from "./slices/navigationSlice";
import retailerRequestReducer from "./slices/retailerRequestSlice";
import orderConfirmReducer from "./slices/orderConfirmSlice";
import tradeShowReducer from "./slices/tradeShowSlice";
import retailerTradeShowCartReducer from "./slices/retailerTradeShowCartSlice";
import salesTradeShowCartReducer from "./slices/salesTradeShowCartSlice";
import routeOptimizationReducer from "./slices/routeOptimizationSlice";
import returnPriceChangeReducer from "./slices/returnPriceChangeSlice";
import { useDispatch, useSelector } from 'react-redux';

const formPersistConfig = {
  key: 'form',
  storage,
  whitelist: ['data'] // only data will be persisted
};

const authPersistConfig = {
  key: 'auth',
  storage,
  // NOTE: showTradeShow is intentionally NOT persisted. It must be recomputed
  // each session from the API (setSalesSession / hasMultipleStore / login), so a
  // stale "true" from a previous customer/session can never leak across reloads
  // and wrongly show the Trade Show tab when the current API response says false.
  whitelist: ['token', 'isAuthenticated', 'role', 'wareHouseDetail', 'storeDetail', 'signUpData', 'module', 'selectedCustomer', 'isSessionActive','logo', 'allowDiscount', 'discountLimit', 'allowDeliveryCharge', 'activeTradeShows', 'hasMultipleStore', 'stores', 'selectedStore', 'priceBook', 'language', 'popupRead'] // only persist these fields
};

const salesDashboardPersistConfig = {
  key: 'salesDashboard',
  storage,
  whitelist: ['newItems', 'discountedItems', 'popularItems']
};
const dashboardPersistConfig = {
  key: 'dashboard',
  storage,
  whitelist: ['newItems', 'discountedItems', 'popularItems']
};

const cartPersistConfig = {
  key: 'cart',
  storage,
  whitelist: ['items', 'count', 'totalItems', 'totalAmountWithTax', 'totalAmount', 'userItemLimitQty', 'userLimitMinOrderAmount']
};

const salesCartPersistConfig = {
  key: 'salesCart',
  storage,
  whitelist: ['items', 'count', 'totalItems', 'totalAmountWithTax', 'totalAmount', 'userItemLimitQty', 'userLimitMinOrderAmount']
};

const salesPriceBookCartPersistConfig = {
  key: 'salesPriceBookCart',
  storage,
  whitelist: ['items', 'count', 'totalItems', 'totalAmountWithTax', 'totalAmount', 'userItemLimitQty', 'userLimitMinOrderAmount']
};

const routeOptimizationPersistConfig = {
  key: 'routeOptimization',
  storage,
  whitelist: [
    'draftSelectedOrdersById',
    'draftRoutesById',
    'draftIdCounter',
    'activeDraftId',
    'routeEndDay',
    'transferContext',
    'customRoutePoints',
    'selectedOrders',
    'selectedDriverId',
    'selectedDriverName',
    'selectedVehicleId',
    'selectedVehicleLabel',
    'googleMapRoute',
    'googleMapOptimizedStops',
    'googleMapSnapshotKey',
    'driverVehicleAssignmentsById',
    'multiPreviewRoutes',
    'multiPreviewSnapshotKey',
    'lastFetchedMultiPreview',
    'activeMapViewDriverId',
    'manualSelectedDriverId',
    'manualSelectedVehicleId',
    'manualStops',
  ],
};

const tradeShowPersistConfig = {
  key: 'tradeShow',
  storage,
  whitelist: [
    'currentTradeShow',
    'selectedVendorIds',
    'createDraft',
    'activeStep',
    'selectedRetailerIds',
    'appliedItems',
    'savedTradeShowItems',
    'weekProductAssignments',
    'summaryData',
    'lastSavedVendorIds',
    'lastSavedRetailerIds',
    'lastSavedDeliveryItemNumbers',
    'lastSavedItemNumbers',
  ],
};

const returnPriceChangePersistConfig = {
  key: 'returnPriceChange',
  storage,
  // allowChangePrice is intentionally NOT persisted - it is re-fetched from the
  // API whenever the return order / return cart page mounts (incl. hard refresh).
  // changedPrices persists so the 1 hour expiry window survives a refresh.
  whitelist: ['changedPrices']
};

const persistedFormReducer = persistReducer(formPersistConfig, formReducer);
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedSalesDashboardReducer = persistReducer(salesDashboardPersistConfig, salesDashboardReducer);
const persistDashboardReducer = persistReducer(dashboardPersistConfig, dashboardReducer);
const persistedCartReducer = persistReducer(cartPersistConfig, cartReducer);
const persistedSalesCartReducer = persistReducer(salesCartPersistConfig, salesCartReducer);
const persistedSalesPriceBookCartReducer = persistReducer(salesPriceBookCartPersistConfig, salesPriceBookCartReducer);
const persistedTradeShowReducer = persistReducer(tradeShowPersistConfig, tradeShowReducer);
const persistedRouteOptimizationReducer = persistReducer(
  routeOptimizationPersistConfig,
  routeOptimizationReducer
);
const persistedReturnPriceChangeReducer = persistReducer(returnPriceChangePersistConfig, returnPriceChangeReducer);

export const store = configureStore({
  reducer: {
    form: persistedFormReducer,
    theme: themeReducer,
    auth: persistedAuthReducer,
    layout: layoutReducer,
    cart: persistedCartReducer,
    salesCart: persistedSalesCartReducer,
    salesPriceBookCart: persistedSalesPriceBookCartReducer,
    // Pre-book carts: one pre-book at a time, intentionally NOT persisted
    preBookCart: preBookCartReducer,
    salesPreBookCart: salesPreBookCartReducer,
    globalPopup: globalPopupReducer,
    dashboard: persistDashboardReducer,
    salesDashboard: persistedSalesDashboardReducer,
    notification: notificationReducer,
    navigation: navigationReducer,
    retailerRequest: retailerRequestReducer,
    orderConfirm: orderConfirmReducer,
    tradeShow: persistedTradeShowReducer,
    retailerTradeShowCart: retailerTradeShowCartReducer,
    salesTradeShowCart: salesTradeShowCartReducer,
    routeOptimization: persistedRouteOptimizationReducer,
    returnPriceChange: persistedReturnPriceChangeReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export const persistor = persistStore(store);

// For TypeScript
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector = <T>(selector: (state: RootState) => T): T => {
  return useSelector(selector);
};
